import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';

const GAUGE_MAX_SEGMENTS = 7;

// Figma text colour of the table values (the theme's text.primary is darker).
const VALUE_COLOR = '#333C47';

// Low presence is good news, so the scale runs green → yellow → red as the rate climbs.
export function presenceColor(value: number): string {
    if (value > 66) return '#dc1818';
    if (value > 33) return '#efcf61';
    return '#118d57';
}

/**
 * The integer the cell prints. Shared with the column's `valueGetter` so filtering on the rate
 * compares against the number the user actually reads, not the raw float behind it.
 */
export const presenceDisplayValue = (value: number) => Math.round(Math.min(100, Math.max(0, value)));

export function PaidPresenceRateCell({ value }: { value: number }) {
    const clamped = Math.min(100, Math.max(0, value));
    return (
        <Stack direction="row" alignItems="center" spacing={1} width="100%">
            <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: VALUE_COLOR }}>
                {presenceDisplayValue(value)}%
            </Typography>
            <Box sx={{ flex: 1, height: 6, borderRadius: 6, bgcolor: 'rgba(145,158,171,0.2)', overflow: 'hidden' }}>
                <Box sx={{ width: `${clamped}%`, height: '100%', borderRadius: 24, bgcolor: presenceColor(clamped) }} />
            </Box>
        </Stack>
    );
}

export function ActiveDaysGaugeCell({ activeDays, totalDays }: { activeDays: number; totalDays: number }) {
    const segments = Math.min(totalDays || GAUGE_MAX_SEGMENTS, GAUGE_MAX_SEGMENTS);
    const filled = totalDays <= GAUGE_MAX_SEGMENTS
        ? activeDays
        : Math.round((activeDays / (totalDays || 1)) * GAUGE_MAX_SEGMENTS);
    return (
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end" width="100%">
            <Typography variant="body2" fontWeight={500} color={VALUE_COLOR}>{activeDays}</Typography>
            <Stack direction="row" spacing="4px">
                {Array.from({ length: segments }).map((_, i) => (
                    <Box
                        key={i}
                        data-testid="gauge-segment"
                        data-active={i < filled ? 'true' : 'false'}
                        sx={{ width: 4, height: 14, borderRadius: '2px', bgcolor: i < filled ? '#1e88e5' : '#dfe3e8' }}
                    />
                ))}
            </Stack>
        </Stack>
    );
}

// These pills are not clickable, but the theme's MuiChip override repaints every filled/default
// chip with `grey[700]` on hover from the component root (overrides/components/chip.tsx) — MUI's
// own hover is scoped to `.MuiChip-clickable`, that one isn't. Pinned back to the base tint so the
// pill stays inert, like the search-term chips below.
const placeChipSx = {
    bgcolor: 'rgba(183,220,251,0.2)', borderRadius: '28px', height: 'auto',
    fontSize: 14, fontWeight: 500, color: '#333c47',
    // Figma: 4px 6px around an 18.2px line, a 26px pill.
    '& .MuiChip-label': { padding: '4px 6px', lineHeight: 1.3 },
    '&:hover': { bgcolor: 'rgba(183,220,251,0.2)' },
};

type PlaceCounts = { titleOnly: number; descOnly: number; both: number };

/** Every place a cell can show, in cell order — the filter's options. */
export const PLACE_OPTIONS = ['Title', 'Description', 'Title + Description'] as const;

/**
 * The places a row infringes on. The chips abbreviate the last one when they are crowded, so this
 * returns the unabbreviated labels: it is the row's filterable value, and `PLACE_OPTIONS` is the
 * list the filter offers.
 */
export function placeLabels({ titleOnly, descOnly, both }: PlaceCounts): string[] {
    return [
        titleOnly > 0 && 'Title',
        descOnly > 0 && 'Description',
        both > 0 && 'Title + Description',
    ].filter((label): label is string => typeof label === 'string');
}

export function PlaceChipsCell({ titleOnly, descOnly, both }: PlaceCounts) {
    const labels = placeLabels({ titleOnly, descOnly, both });
    const chips = labels.map((label) =>
        label === 'Title + Description' && labels.length > 1 ? 'Title + Descrip.' : label
    );
    return (
        <Stack direction="row" spacing={1}>
            {chips.map((label) => <Chip key={label} label={label} size="small" sx={placeChipSx} />)}
        </Stack>
    );
}

// A clickable MUI Chip repaints itself on hover with `alpha(palette.action.selected, …)` — a generic
// grey unrelated to the chip's own colour — and raises itself with a shadow on `:active`. Both are
// pinned back to the base tint here so the chip never reacts visually to hover, focus or press.
const searchTermChipSx = {
    bgcolor: 'rgba(145,158,171,0.1)',
    borderRadius: '32px',
    height: 'auto',
    fontSize: 14,
    fontWeight: 400,
    color: '#333c47',
    // Figma: 2px 8px around a 22px line, a 26px pill.
    '& .MuiChip-label': { padding: '2px 8px', lineHeight: '22px' },
    flexShrink: 0,
    '&:hover': { bgcolor: 'rgba(145,158,171,0.1)' },
    '&.Mui-focusVisible': { bgcolor: 'rgba(145,158,171,0.1)' },
    '&:active': { bgcolor: 'rgba(145,158,171,0.1)', boxShadow: 'none' },
};

const CHIP_GAP = 8;
// A row of the grid never holds more chips than this: the terms past it are not even measured
// (a domain can carry hundreds), they go straight into the "+n".
const MAX_MEASURED_TERMS = 30;

const moreBadgeSx = { display: 'flex', alignItems: 'center', px: 1 };

function MoreCount({ count }: { count: number }) {
    return (
        <Typography variant="body2" sx={{ fontSize: 15, color: '#2f3843', fontVariantNumeric: 'tabular-nums' }}>
            +{count}
        </Typography>
    );
}

// "Search Terms" popover, opened by hovering a chip or the "+n" (Figma "Signal 2026 - Q3", node
// 6876:4641).
const popoverSx = {
    bgcolor: '#FCFCFD',
    border: '1px solid #2A323B',
    borderRadius: '8px',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.2)',
    color: VALUE_COLOR,
    p: 0,
    overflow: 'hidden',
    // Figma frame width; MUI caps tooltips at 300px.
    width: 336,
    maxWidth: 'none',
};

const RATE_COLOR = '#637381';

export type SearchTermRates = Record<string, number | null | undefined>;

/** "82%"; "<1%" for a presence too small to round to 1; "–" when there is no rate. */
function formatRate(rate: number | null | undefined): string {
    if (rate == null) return '–';
    const rounded = Math.round(rate);
    return rounded === 0 && rate > 0 ? '<1%' : `${rounded}%`;
}

/** Highest Paid PR first, terms without one last; ties keep the cell's order. */
function rankByRate(terms: string[], rates: SearchTermRates): string[] {
    const rank = (term: string) => rates[term] ?? -Infinity;
    return [...terms].sort((a, b) => rank(b) - rank(a));
}

function SearchTermsPopover({ terms, rates, showHint }: {
    terms: string[]; rates: SearchTermRates; showHint: boolean;
}) {
    return (
        <>
            <Stack spacing="6px" sx={{ pt: '14px', px: 2, lineHeight: 1 }}>
                <Stack direction="row" alignItems="center">
                    <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500, lineHeight: 1, color: VALUE_COLOR }}>
                        Search Terms
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1, color: RATE_COLOR }}>
                        Paid PR
                    </Typography>
                </Stack>
                {showHint && (
                    <Typography sx={{ fontSize: 12, fontStyle: 'italic', lineHeight: 1, color: '#919EAB', whiteSpace: 'nowrap' }}>
                        Click on the cell to See Ads for these Search terms
                    </Typography>
                )}
            </Stack>
            <Box component="ul" sx={{ m: 0, p: 1.5, listStyle: 'none', maxHeight: 320, overflowY: 'auto' }}>
                {rankByRate(terms, rates).map((term) => (
                    <Stack key={term} component="li" direction="row" alignItems="center" spacing={0.5} sx={{ p: 0.5 }}>
                        <Typography noWrap sx={{ flex: 1, fontSize: 14, fontWeight: 400, lineHeight: '22px', color: VALUE_COLOR }}>
                            {term}
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: '22px', color: RATE_COLOR, whiteSpace: 'nowrap' }}>
                            {formatRate(rates[term])}
                        </Typography>
                    </Stack>
                ))}
            </Box>
        </>
    );
}

/** How many leading chips fit in `available` px. The row shows a prefix, so it stops at the first misfit. */
function fittingCount(widths: number[], available: number): number {
    let used = -CHIP_GAP;
    for (let i = 0; i < widths.length; i += 1) {
        used += CHIP_GAP + widths[i];
        // Half a pixel of slack absorbs the sub-pixel rounding of the measured widths.
        if (used > available + 0.5) return i;
    }
    return widths.length;
}

type SearchTermsLayout = { rendered: number; hidden: number; maxChipWidth: number };

/**
 * Every chip while they all fit. Past that, the "+n" badge takes the right end of the row and only
 * the chips that fit whole beside it are shown: the next one would be cropped, so it counts as
 * hidden too. A chip wider than its room ellipsizes down to it, so a long first term still shows
 * rather than a lone "+n".
 */
function searchTermsLayout(
    chipWidths: number[], termCount: number, cellWidth: number, badgeWidth: number,
): SearchTermsLayout {
    const fittingIn = (room: number) => fittingCount(chipWidths.map((width) => Math.min(width, room)), room);
    if (fittingIn(cellWidth) === termCount) {
        return { rendered: termCount, hidden: 0, maxChipWidth: cellWidth };
    }
    const room = cellWidth - badgeWidth;
    const rendered = fittingIn(room);
    return { rendered, hidden: termCount - rendered, maxChipWidth: room };
}

export function SearchTermsCell({ terms, rates = {}, onTermClick, onCellClick }: {
    terms: string[];
    /** Paid PR of the domain on each term, for the popover. */
    rates?: SearchTermRates;
    onTermClick?: (term: string) => void;
    /** A click anywhere in the cell but on a chip (the popover says so). */
    onCellClick?: () => void;
}) {
    const cellRef = useRef<HTMLDivElement | null>(null);
    const chipsMeasureRef = useRef<HTMLDivElement | null>(null);
    const badgeMeasureRef = useRef<HTMLDivElement | null>(null);
    // `null` while the cell has no width yet (and in jsdom): every chip, no badge.
    const [layout, setLayout] = useState<SearchTermsLayout | null>(null);
    const termsKey = terms.join('\n');

    useLayoutEffect(() => {
        const cell = cellRef.current;
        const chipsMeasure = chipsMeasureRef.current;
        const badgeMeasure = badgeMeasureRef.current;
        if (!cell || !chipsMeasure || !badgeMeasure) return undefined;

        const compute = () => {
            const cellWidth = cell.getBoundingClientRect().width;
            setLayout(cellWidth > 0
                ? searchTermsLayout(
                    Array.from(chipsMeasure.children, (chip) => chip.getBoundingClientRect().width),
                    terms.length,
                    cellWidth,
                    badgeMeasure.getBoundingClientRect().width,
                )
                : null);
        };

        compute();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(compute);
        observer.observe(cell);
        // The chips change width when the web font lands.
        observer.observe(chipsMeasure);
        return () => observer.disconnect();
    }, [termsKey, terms.length]);

    const visible = layout ? terms.slice(0, layout.rendered) : terms;
    const hidden = layout?.hidden ?? 0;
    return (
        <Tooltip
            placement="bottom-start"
            title={terms.length ? <SearchTermsPopover terms={terms} rates={rates} showHint={!!onCellClick} /> : ''}
            componentsProps={{ tooltip: { sx: popoverSx } }}
        >
        <Box
            ref={cellRef}
            data-testid="search-terms-cell"
            onClick={onCellClick}
            sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', cursor: onCellClick ? 'pointer' : undefined }}
        >
            <Stack direction="row" alignItems="center" sx={{ height: '100%', overflow: 'hidden', gap: `${CHIP_GAP}px` }}>
                {visible.map((term) => (
                    <Chip
                        key={term}
                        label={term}
                        size="small"
                        onClick={onTermClick ? (event) => {
                            // The chip opens its own term: the cell's click would open them all.
                            event.stopPropagation();
                            onTermClick(term);
                        } : undefined}
                        sx={{ ...searchTermChipSx, maxWidth: layout ? `${layout.maxChipWidth}px` : '100%' }}
                    />
                ))}
            </Stack>
            {hidden > 0 && (
                <Box
                    data-testid="search-terms-more"
                    sx={{
                        ...moreBadgeSx,
                        position: 'absolute', right: 0, top: 0, height: '100%',
                        bgcolor: 'rgba(240,240,240,0.7)', backdropFilter: 'blur(1px)',
                        cursor: 'default',
                    }}
                >
                    <MoreCount count={hidden} />
                </Box>
            )}
            {/* Unclipped copies the layout is measured on: every chip, and the badge at its widest. */}
            <Box aria-hidden sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', height: 0, overflow: 'hidden' }}>
                <Box ref={chipsMeasureRef} sx={{ display: 'flex', gap: `${CHIP_GAP}px`, width: 'max-content' }}>
                    {terms.slice(0, MAX_MEASURED_TERMS).map((term) => (
                        <Chip key={term} label={term} size="small" sx={searchTermChipSx} />
                    ))}
                </Box>
                <Box ref={badgeMeasureRef} sx={{ ...moreBadgeSx, width: 'max-content' }}>
                    <MoreCount count={terms.length} />
                </Box>
            </Box>
        </Box>
        </Tooltip>
    );
}

const reportButtonSx = { width: 26, height: 26, borderRadius: '6px', bgcolor: 'rgba(226,229,233,0.62)' };

export function ReportCell({ hasReport, onDownload, busy = false }: {
    hasReport: boolean;
    onDownload: () => void;
    busy?: boolean;
}) {
    const label = hasReport ? 'Download report' : 'No report available';
    return (
        <Stack direction="row" alignItems="center" justifyContent="center" width="100%">
            {busy ? (
                <Box sx={{ ...reportButtonSx, bgcolor: 'rgba(226,229,233,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={14} />
                </Box>
            ) : (
                <Tooltip title={label}>
                    {/* A disabled button fires no events: the span keeps the tooltip working. */}
                    <span>
                        <IconButton
                            size="small"
                            aria-label={label}
                            disabled={!hasReport}
                            onClick={onDownload}
                            sx={{ ...reportButtonSx, opacity: hasReport ? 1 : 0.2 }}
                        >
                            <Iconify icon="eva:download-outline" width={14} />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Stack>
    );
}
